import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import CreditNotePDF from './CreditNotePDF'
import { useLocation, useOutletContext } from 'react-router-dom'
import CreditNote from './CreditNote'
import { getObject, getObjectsWithFilter } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import Toolbar from '../../toolbar/Toolbar'
import SettingsIcon from '@mui/icons-material/Settings'
import EuroIcon from '@mui/icons-material/Euro'
import EmailIcon from '@mui/icons-material/Email'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import PDFReader from '../../pdfReader/PDFReader'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import { fileToBase64, updateAttachments, updateDocumentStatus } from '../../../utils/documentManager'
import StatusDialog from '../../dialogs/StatusDialog'
import Loading from '../../common/Loading'
import StatusField from '../../fields/StatusField'
import PaymentDialog from '../../dialogs/PaymentDialog'
import NotFound from '../../common/NotFound'
import { pdf } from '@react-pdf/renderer'
import DocumentPreview from '../../document/DocumentPreview'
import Outcome from '../Accounting/Outcome'

const ShowCreditNote = () => {
  const [creditNote, setCreditNote] = useState({})
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [statusList, setStatusList] = useState([])
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [pdfUrl, setPdfUrl] = useState('')
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)

  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()

  const openStatusDialog = useCallback(() => setIsStatusDialogOpen(true), [])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== creditNote.status) {
        setCreditNote((prev) => ({ ...prev, status: newValue }))
        setIsStatusChanged(true)
      }
    },
    [creditNote.status]
  )

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'avoir',
        object: creditNote,
        objectType: 'creditNote',
        user,
        company,
        customer: creditNote.customer,
        template: company.credit_email_template,
        component: (
          <CreditNotePDF creditNote_param={creditNote} company_param={company} logo_param={img} settings={settings} />
        ),
        setFiles,
        subjectPrefix: 'Votre avoir',
        statusToUpdate: 70,
        updateStatus: (newStatus) => {
          setCreditNote((prev) => ({ ...prev, status: newStatus }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [creditNote, user, company, img, settings])

  const openPaymentDialog = useCallback(() => setIsPaymentDialogOpen(true), [])

  const closePaymentDialog = useCallback(
    (newValue) => {
      setIsPaymentDialogOpen(false)
      if (newValue?.id !== undefined && newValue.id === creditNote.id) {
        setCreditNote((prev) => ({ ...prev, status: 80 }))
        setIsStatusChanged(true)
      }
    },
    [creditNote.id]
  )

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'creditNotes',
      title: () => {
        return `Avoir ${creditNote.creditNote_number}`
      },
      accessRight: userRight,
      actions: {
        edit: true,
        editDisabled: () => creditNote.status >= 70,
        files: true,
        back: true,
        audits: true,
        list: true
      },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: sendEmail,
          disabled: () => !creditNote?.customer?.email
        },
        {
          icon: () => <EuroIcon />,
          tooltip: 'Payer',
          onClick: openPaymentDialog,
          disabled: () => creditNote.status >= 80 || creditNote.status < 70
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: openStatusDialog,
          disabled: () => creditNote.status >= 80
        }
      ],
      customComponentsLeft: [
        {
          component: () => <StatusField value={creditNote.status} type='creditNotes' statusList={statusList} />
        }
      ]
    }),
    [userRight, creditNote, statusList, sendEmail, openStatusDialog, openPaymentDialog]
  )

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('creditNotes', creditNote, user, toast, setIsStatusChanged)
  }, [isStatusChanged, creditNote, user])

  useEffect(() => {
    const creditNoteId = location?.state?.id
    if (!creditNoteId) {
      toast.error('Avoir introuvable.', { autoClose: 2000 })
      return
    }

    const fetchAll = async () => {
      try {
        const [statusRes, creditNoteRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'creditNotes' }),
          getObject('creditNotes', creditNoteId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setCreditNote(creditNoteRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (creditNoteRes.data.attachments) {
          setFiles(creditNoteRes.data.attachments)
          const attach = creditNoteRes.data.attachments.find((f) => f.type === 'creditNote')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  useEffect(() => {
    setCreditNote((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    if (!creditNote?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = creditNote.attachments?.find((f) => f.type === 'creditNote')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <CreditNotePDF creditNote_param={creditNote} company_param={company} logo_param={img} settings={settings} />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [creditNote, company, img, settings])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!creditNote.id) return <NotFound title='Avoir introuvable' />

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar
        options={toolbarOptions}
        object={creditNote}
        setObject={setFiles}
        setParentObject={setCreditNote}
        parentObject={creditNote}
      />

      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`avoir_${creditNote?.creditNote_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={CreditNote} document={creditNote} />
        </Box>
      )}

      <StatusDialog
        statusList={statusList}
        open={isStatusDialogOpen}
        onClose={closeStatusDialog}
        type='creditNotes'
        value={creditNote.status}
      />

      <PaymentDialog
        FormComponent={Outcome}
        defaultTitle='Payer avoir'
        open={isPaymentDialogOpen}
        data={creditNote}
        onClose={closePaymentDialog}
      />
    </Box>
  )
}

export default ShowCreditNote
